import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { Croissant, Loader2 } from "lucide-react";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) {
    return <Navigate to="/" replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const res = await login(email.trim().toLowerCase(), password);
    setBusy(false);
    if (res.ok) {
      toast.success("Bienvenue !");
      navigate("/");
    } else {
      toast.error(res.error || "Connexion impossible");
    }
  };

  return (
    <div className="min-h-screen flex items-stretch">
      {/* Visuel gauche */}
      <div
        className="hidden lg:flex flex-1 relative items-end p-12 text-white"
        style={{
          backgroundImage: `linear-gradient(120deg, rgba(62,39,35,0.55), rgba(209,108,63,0.45)), url('https://www.lacantinedegeraldine.be/cdn/shop/files/LaCantineDeGeraldine_Mars_2026_-13.jpg?v=1774860820&width=1800')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        data-testid="login-hero"
      >
        <div className="max-w-md">
          <Croissant className="w-10 h-10 mb-6 opacity-90" />
          <h1 className="font-display text-4xl xl:text-5xl leading-tight drop-shadow">
            Le planning, plus jamais sur WhatsApp.
          </h1>
          <p className="mt-4 text-white/90 text-base xl:text-lg">
            Dispos, plannings et heures prestées — réunis pour La Cantine de Géraldine.
          </p>
        </div>
      </div>

      {/* Formulaire */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12 bg-[#FFFFFF]">
        <Card className="w-full max-w-md card-soft border-0">
          <CardContent className="p-7 sm:p-10">
            <div className="flex items-center gap-3 mb-8">
              <img
                src="/logo-cantine.png"
                alt="La Cantine de Géraldine"
                className="h-14 w-auto object-contain"
                data-testid="login-logo"
              />
            </div>

            <h2 className="font-display text-2xl text-[#1A2B27] mb-1">Connexion</h2>
            <p className="text-sm text-[#6b7f79] mb-7">
              Accès au planning, aux disponibilités et aux heures.
            </p>

            <form onSubmit={submit} className="space-y-5">
              <div>
                <Label htmlFor="email" className="label-tiny">Email</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="prenom@lacantinedegeraldine.be"
                  data-testid="login-email-input"
                  className="mt-2 h-12 rounded-xl border-[#DDE7E4] focus-visible:ring-[#2D5951]"
                />
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="label-tiny">Mot de passe</Label>
                  <Link
                    to="/forgot-password"
                    data-testid="login-forgot-link"
                    className="text-xs font-semibold text-[#2D5951] hover:underline"
                  >
                    Oublié ?
                  </Link>
                </div>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password-input"
                  className="mt-2 h-12 rounded-xl border-[#DDE7E4] focus-visible:ring-[#2D5951]"
                />
              </div>

              <Button
                type="submit"
                disabled={busy}
                data-testid="login-submit-button"
                className="w-full h-12 rounded-full bg-[#2D5951] hover:bg-[#1F423C] text-white font-semibold tracking-wide transition-all duration-200 hover:-translate-y-0.5 shadow-md"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Se connecter"}
              </Button>
            </form>

            <div className="mt-8 pt-6 border-t border-[#DDE7E4] text-xs text-[#94ADA7] leading-relaxed">
              <p>Accès réservé à l'équipe de La Cantine de Géraldine. Un souci de connexion ? Contacte Charles.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
